/**
 * ChatWidget - Floating support chat for institution users
 *
 * Features:
 * - Floating chat bubble with unread badge
 * - AI-first support with human escalation
 * - Real-time message updates
 * - Conversation history
 * - Rating system for resolved conversations
 */

import { useState, useRef, useEffect, FormEvent } from 'react';
import { clsx } from 'clsx';
import { useSupport } from '@/hooks/useSupport';
import type { SupportMessage, SupportConversation } from '@/services/support';
import styles from './ChatWidget.module.css';

// Category icons mapping
const categoryIcons: Record<string, React.ReactNode> = {
  technical: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M6.5 1.5C4.29086 1.5 2.5 3.29086 2.5 5.5C2.5 6.7 3 7.8 3.8 8.6L1.5 14.5L4.5 13L6.5 14.5L8.5 13L11.5 14.5L9.2 8.6C10 7.8 10.5 6.7 10.5 5.5C10.5 3.29086 8.70914 1.5 6.5 1.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  account: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M3 13.5C3 10.7386 5.23858 8.5 8 8.5C10.7614 8.5 13 10.7386 13 13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  training: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M2 3.5H10.5V12.5C10.5 13.0523 10.0523 13.5 9.5 13.5H3C2.44772 13.5 2 13.0523 2 12.5V3.5Z" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M10.5 5H13C13.5523 5 14 5.44772 14 6V12.5C14 13.0523 13.5523 13.5 13 13.5H10.5" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M4.5 7H8M4.5 9.5H7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  compliance: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M8 1.5L2 4V7.5C2 11.5 4.5 14 8 14.5C11.5 14 14 11.5 14 7.5V4L8 1.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M5.5 8L7 9.5L10.5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  feature: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M8 2V4M8 12V14M14 8H12M4 8H2M12.2 3.8L10.8 5.2M5.2 10.8L3.8 12.2M12.2 12.2L10.8 10.8M5.2 5.2L3.8 3.8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  ),
  other: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M6 6C6 5 7 4 8 4C9 4 10 5 10 6C10 7 9 7.5 8 8V9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="8" cy="11.5" r="0.5" fill="currentColor"/>
    </svg>
  ),
};

// Status badge colors
const statusColors: Record<string, string> = {
  open: 'var(--support-status-open)',
  assigned: 'var(--support-status-assigned)',
  pending_user: 'var(--support-status-pending)',
  resolved: 'var(--support-status-resolved)',
  closed: 'var(--support-status-closed)',
};

export function ChatWidget() {
  const {
    categories,
    conversations,
    activeConversation,
    messages,
    unreadCount,
    isLoading,
    isSending,
    error,
    startConversation,
    sendChatMessage,
    selectConversation,
    closeChatConversation,
    rateChat,
    clearActiveConversation,
    refreshConversations,
    isWidgetOpen,
    setWidgetOpen,
    askAIQuestion,
  } = useSupport({ autoFetch: false }); // Don't auto-fetch on mount - fetch when widget opens

  const [hasInitialized, setHasInitialized] = useState(false);

  const [view, setView] = useState<'list' | 'chat' | 'new' | 'quickAI'>('list');
  const [newSubject, setNewSubject] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [chatInput, setChatInput] = useState('');
  const [showRating, setShowRating] = useState(false);
  const [selectedRating, setSelectedRating] = useState(0);
  const [ratingFeedback, setRatingFeedback] = useState('');

  // Quick AI Help state
  const [quickAIInput, setQuickAIInput] = useState('');
  const [quickAIHistory, setQuickAIHistory] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>([]);
  const [isAILoading, setIsAILoading] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch data when widget is first opened
  useEffect(() => {
    if (isWidgetOpen && !hasInitialized) {
      setHasInitialized(true);
      refreshConversations();
    }
  }, [isWidgetOpen, hasInitialized, refreshConversations]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isWidgetOpen && view === 'chat' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isWidgetOpen, view]);

  // Update view when active conversation changes
  useEffect(() => {
    if (activeConversation) {
      setView('chat');
    }
  }, [activeConversation]);

  const handleToggle = () => {
    setWidgetOpen(!isWidgetOpen);
    if (!isWidgetOpen && !activeConversation) {
      setView('list');
    }
  };

  const handleNewConversation = () => {
    setView('new');
    setNewSubject('');
    setNewMessage('');
    // Set default category if categories are already loaded
    if (categories.length > 0) {
      setNewCategory(categories[0].id);
    } else {
      setNewCategory('');
    }
  };

  // Set default category when categories finish loading (for new conversation view)
  useEffect(() => {
    if (view === 'new' && categories.length > 0 && !newCategory) {
      setNewCategory(categories[0].id);
    }
  }, [view, categories, newCategory]);

  const handleSubmitNewConversation = async (e: FormEvent) => {
    e.preventDefault();
    if (!newCategory || !newSubject.trim() || !newMessage.trim()) return;

    const conversation = await startConversation(newCategory, newSubject, newMessage);
    if (conversation) {
      setView('chat');
      setNewSubject('');
      setNewMessage('');
      setNewCategory('');
    }
  };

  const handleSendMessage = async (e: FormEvent) => {
    e.preventDefault();
    if (!chatInput.trim() || isSending) return;

    const message = chatInput;
    setChatInput('');
    await sendChatMessage(message);
  };

  const handleSelectConversation = async (id: string) => {
    await selectConversation(id);
    setView('chat');
  };

  const handleBackToList = () => {
    clearActiveConversation();
    setView('list');
    setShowRating(false);
    // Clear quick AI history when going back
    setQuickAIHistory([]);
    setQuickAIInput('');
  };

  const handleCloseConversation = async () => {
    await closeChatConversation();
    setShowRating(true);
  };

  const handleSubmitRating = async () => {
    if (selectedRating > 0) {
      await rateChat(selectedRating, ratingFeedback);
      setShowRating(false);
      setSelectedRating(0);
      setRatingFeedback('');
    }
  };

  // Quick AI Help handlers
  const handleQuickAI = () => {
    setView('quickAI');
    setQuickAIInput('');
  };

  const handleAskQuickAI = async (e: FormEvent) => {
    e.preventDefault();
    if (!quickAIInput.trim() || isAILoading) return;

    const question = quickAIInput.trim();
    setQuickAIInput('');
    setIsAILoading(true);

    // Add user question to history
    setQuickAIHistory(prev => [...prev, { role: 'user', content: question }]);

    try {
      const response = await askAIQuestion(question);
      if (response) {
        setQuickAIHistory(prev => [...prev, { role: 'assistant', content: response }]);
      } else {
        setQuickAIHistory(prev => [...prev, { role: 'assistant', content: "I couldn't process your question. Please try again or start a conversation with our support team." }]);
      }
    } catch {
      setQuickAIHistory(prev => [...prev, { role: 'assistant', content: "Something went wrong. Please try again." }]);
    } finally {
      setIsAILoading(false);
    }
  };

  const handleEscalateToHuman = () => {
    // Pre-fill new conversation with the AI conversation context
    const context = quickAIHistory.map(msg =>
      `${msg.role === 'user' ? 'User' : 'AI'}: ${msg.content}`
    ).join('\n\n');

    setNewMessage(`[Previous AI conversation]\n${context}\n\n[Additional details]\n`);
    setNewCategory('other');
    setView('new');
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = diff / (1000 * 60 * 60);

    if (hours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (hours < 48) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const renderMessage = (msg: SupportMessage) => {
    const isUser = msg.sender_type === 'user';
    const isSystem = msg.sender_type === 'system';
    const isAI = msg.message_type === 'ai_response';

    return (
      <div
        key={msg.id}
        className={clsx(
          styles.message,
          isUser && styles.userMessage,
          isSystem && styles.systemMessage,
          isAI && styles.aiMessage
        )}
      >
        {!isUser && !isSystem && (
          <div className={styles.messageAvatar}>
            {isAI ? (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 1L2 4v4c0 4 3 7 6 8 3-1 6-4 6-8V4L8 1z"/>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <circle cx="8" cy="5" r="3"/>
                <path d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6"/>
              </svg>
            )}
          </div>
        )}
        <div className={styles.messageContent}>
          {!isUser && !isSystem && (
            <div className={styles.messageSender}>
              {isAI ? 'OHCS AI Assistant' : msg.sender_name}
            </div>
          )}
          <div className={styles.messageText}>{msg.content}</div>
          <div className={styles.messageTime}>{formatTime(msg.created_at)}</div>
        </div>
      </div>
    );
  };

  const renderConversationItem = (conv: SupportConversation) => (
    <button
      key={conv.id}
      className={clsx(styles.conversationItem, activeConversation?.id === conv.id && styles.active)}
      onClick={() => handleSelectConversation(conv.id)}
    >
      <div className={styles.conversationIcon}>
        {categoryIcons[conv.category_id] || categoryIcons.other}
      </div>
      <div className={styles.conversationInfo}>
        <div className={styles.conversationSubject}>{conv.subject}</div>
        <div className={styles.conversationMeta}>
          <span
            className={styles.conversationStatus}
            style={{ color: statusColors[conv.status] }}
          >
            {conv.status.replace('_', ' ')}
          </span>
          <span className={styles.conversationTime}>
            {conv.last_message_at ? formatTime(conv.last_message_at) : formatTime(conv.created_at)}
          </span>
        </div>
      </div>
      {(conv.unread_count || 0) > 0 && (
        <div className={styles.unreadBadge}>{conv.unread_count}</div>
      )}
    </button>
  );

  return (
    <>
      {/* Chat Bubble */}
      <button
        className={clsx(styles.chatBubble, isWidgetOpen && styles.hidden)}
        onClick={handleToggle}
        aria-label="Open support chat"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="currentColor"
          />
        </svg>
        {unreadCount > 0 && <span className={styles.bubbleBadge}>{unreadCount}</span>}
      </button>

      {/* Chat Panel */}
      <div className={clsx(styles.chatPanel, isWidgetOpen && styles.open)}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            {view !== 'list' && (
              <button className={styles.backButton} onClick={handleBackToList} aria-label="Back">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
            <div className={styles.headerTitle}>
              {view === 'list' && 'Support Center'}
              {view === 'new' && 'New Conversation'}
              {view === 'quickAI' && 'AI Assistant'}
              {view === 'chat' && (activeConversation?.subject || 'Chat')}
            </div>
            <button className={styles.closeButton} onClick={handleToggle} aria-label="Close">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
          {view === 'chat' && activeConversation && (
            <div className={styles.headerStatus}>
              <span
                className={styles.statusDot}
                style={{ backgroundColor: statusColors[activeConversation.status] }}
              />
              {activeConversation.status === 'assigned'
                ? `Assigned to ${activeConversation.assigned_to_name}`
                : activeConversation.status.replace('_', ' ')}
            </div>
          )}
        </div>

        {/* Content */}
        <div className={styles.content}>
          {error && <div className={styles.errorBanner}>{error}</div>}

          {/* List View */}
          {view === 'list' && (
            <div className={styles.listView}>
              {/* Quick AI Help - Primary Action */}
              <button className={styles.quickAIButton} onClick={handleQuickAI}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 1.5L2.25 4.5V8.25C2.25 12.6 5.0625 16.65 9 17.625C12.9375 16.65 15.75 12.6 15.75 8.25V4.5L9 1.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M6.75 9L8.25 10.5L11.25 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div className={styles.quickAIText}>
                  <span className={styles.quickAITitle}>Ask AI Assistant</span>
                  <span className={styles.quickAIDesc}>Get instant answers from our knowledge base</span>
                </div>
              </button>

              <div className={styles.listDivider}>
                <span>or</span>
              </div>

              <button className={styles.newButton} onClick={handleNewConversation}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Start Support Conversation
              </button>

              {isLoading ? (
                <div className={styles.loading}>Loading conversations...</div>
              ) : conversations.length === 0 ? (
                <div className={styles.emptyState}>
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                    <path d="M42 30C42 31.0609 41.5786 32.0783 40.8284 32.8284C40.0783 33.5786 39.0609 34 38 34H14L6 42V10C6 8.93913 6.42143 7.92172 7.17157 7.17157C7.92172 6.42143 8.93913 6 10 6H38C39.0609 6 40.0783 6.42143 40.8284 7.17157C41.5786 7.92172 42 8.93913 42 10V30Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <p>No conversations yet</p>
                  <p className={styles.emptyHint}>Start a new conversation to get help</p>
                </div>
              ) : (
                <div className={styles.conversationList}>
                  {conversations.map(renderConversationItem)}
                </div>
              )}
            </div>
          )}

          {/* New Conversation Form */}
          {view === 'new' && (
            <form className={styles.newForm} onSubmit={handleSubmitNewConversation}>
              <div className={styles.formGroup}>
                <label htmlFor="category">Category</label>
                <select
                  id="category"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  className={styles.categorySelect}
                  required
                >
                  <option value="">Select a category...</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="subject">Subject</label>
                <input
                  id="subject"
                  type="text"
                  value={newSubject}
                  onChange={(e) => setNewSubject(e.target.value)}
                  placeholder="Brief description of your issue"
                  maxLength={100}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Describe your issue in detail..."
                  rows={4}
                  required
                />
              </div>

              <div className={styles.formHint}>
                Our AI assistant will try to help first. If needed, a human agent will take over.
              </div>

              <button
                type="submit"
                className={styles.submitButton}
                disabled={!newCategory || !newSubject.trim() || !newMessage.trim() || isSending}
              >
                {isSending ? 'Starting...' : 'Start Conversation'}
              </button>
            </form>
          )}

          {/* Quick AI Help View */}
          {view === 'quickAI' && (
            <div className={styles.quickAIView}>
              <div className={styles.quickAIIntro}>
                <div className={styles.aiAvatarLarge}>
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="currentColor">
                    <path d="M16 2L4 8V14.67C4 22.73 9.11 30.17 16 32C22.89 30.17 28 22.73 28 14.67V8L16 2Z"/>
                  </svg>
                </div>
                <h3>OHCS AI Assistant</h3>
                <p>Ask me anything about the Records Management System. I have access to the full knowledge base and can help with:</p>
                <ul className={styles.aiCapabilities}>
                  <li>Document management & digitization</li>
                  <li>Staff records & P-Files</li>
                  <li>Workflows & approvals</li>
                  <li>Security & compliance</li>
                  <li>System features & navigation</li>
                </ul>
              </div>

              <div className={styles.quickAIMessages}>
                {quickAIHistory.map((msg, idx) => (
                  <div
                    key={idx}
                    className={clsx(
                      styles.quickAIMessage,
                      msg.role === 'user' ? styles.userMessage : styles.aiMessage
                    )}
                  >
                    {msg.role === 'assistant' && (
                      <div className={styles.messageAvatar}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                          <path d="M8 1L2 4v4c0 4 3 7 6 8 3-1 6-4 6-8V4L8 1z"/>
                        </svg>
                      </div>
                    )}
                    <div className={styles.messageContent}>
                      <div className={styles.messageText}>{msg.content}</div>
                    </div>
                  </div>
                ))}
                {isAILoading && (
                  <div className={clsx(styles.quickAIMessage, styles.aiMessage)}>
                    <div className={styles.messageAvatar}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M8 1L2 4v4c0 4 3 7 6 8 3-1 6-4 6-8V4L8 1z"/>
                      </svg>
                    </div>
                    <div className={styles.messageContent}>
                      <div className={styles.typingIndicator}>
                        <span></span><span></span><span></span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <form className={styles.quickAIInputForm} onSubmit={handleAskQuickAI}>
                <input
                  type="text"
                  value={quickAIInput}
                  onChange={(e) => setQuickAIInput(e.target.value)}
                  placeholder="Ask a question..."
                  disabled={isAILoading}
                />
                <button type="submit" disabled={!quickAIInput.trim() || isAILoading}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M18.3333 1.66667L9.16667 10.8333M18.3333 1.66667L12.5 18.3333L9.16667 10.8333M18.3333 1.66667L1.66667 7.5L9.16667 10.8333" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </form>

              {quickAIHistory.length > 0 && (
                <button className={styles.escalateButton} onClick={handleEscalateToHuman}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M3 13.5C3 10.7386 5.23858 8.5 8 8.5C10.7614 8.5 13 10.7386 13 13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  Talk to Human Support
                </button>
              )}
            </div>
          )}

          {/* Chat View */}
          {view === 'chat' && (
            <div className={styles.chatView}>
              <div className={styles.messagesContainer}>
                {messages.map(renderMessage)}
                <div ref={messagesEndRef} />
              </div>

              {/* Rating Prompt */}
              {showRating && activeConversation?.status === 'resolved' && !activeConversation.rating && (
                <div className={styles.ratingPrompt}>
                  <p>How was your support experience?</p>
                  <div className={styles.ratingStars}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        className={clsx(styles.starButton, selectedRating >= star && styles.selected)}
                        onClick={() => setSelectedRating(star)}
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill={selectedRating >= star ? 'currentColor' : 'none'}>
                          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    ))}
                  </div>
                  {selectedRating > 0 && (
                    <>
                      <textarea
                        className={styles.feedbackInput}
                        value={ratingFeedback}
                        onChange={(e) => setRatingFeedback(e.target.value)}
                        placeholder="Additional feedback (optional)"
                        rows={2}
                      />
                      <button className={styles.submitRating} onClick={handleSubmitRating}>
                        Submit Rating
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Chat Input */}
              {activeConversation && !['closed', 'resolved'].includes(activeConversation.status) && (
                <form className={styles.chatInputForm} onSubmit={handleSendMessage}>
                  <input
                    ref={inputRef}
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder="Type your message..."
                    disabled={isSending}
                  />
                  <button type="submit" disabled={!chatInput.trim() || isSending}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M18.3333 1.66667L9.16667 10.8333M18.3333 1.66667L12.5 18.3333L9.16667 10.8333M18.3333 1.66667L1.66667 7.5L9.16667 10.8333" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </form>
              )}

              {/* Close Conversation Button */}
              {activeConversation && ['resolved', 'pending_user'].includes(activeConversation.status) && !activeConversation.rating && (
                <button className={styles.closeConvButton} onClick={handleCloseConversation}>
                  Close Conversation
                </button>
              )}
            </div>
          )}
        </div>

        {/* AI Powered Footer */}
        <div className={styles.footer}>
          <span>Powered by OHCS AI</span>
        </div>
      </div>
    </>
  );
}
